import {
  GetPermanentAccessToken,
  getPermanentAccessTokenWithCookie,
  signOutAndClearCookies,
  useAuth,
} from '@ankr.com/auth';
import { useCallback } from 'react';
import * as Sentry from '@sentry/react';

import { CONFIG } from 'modules/api/MultiService';
import { useAppDispatch } from 'store/useAppDispatch';
import { trackAuthenticationCompleted } from 'modules/analytics';

import { AUTH_APP_ID, AUTH_APP_PATH, AUTH_PATH } from '../const';
import { handlePermanentTokenError } from '../utils/handlePermanentTokenError';
import { getOAuthDataFromURL } from '../../oauth/utils/getOAuthDataFromURL';

export interface UseSSOAuthProps {
  hasAPIKeysNotification?: boolean;
  referralCode?: string;
}

export const useSSOAuth = ({
  hasAPIKeysNotification,
  referralCode,
}: UseSSOAuthProps = {}) => {
  const dispatch = useAppDispatch();

  const getPermanentAccessToken = useCallback<GetPermanentAccessToken>(
    async params => {
      const { provider } = getOAuthDataFromURL();

      try {
        const result = await getPermanentAccessTokenWithCookie(params);

        void trackAuthenticationCompleted({ provider });

        return result;
      } catch (error) {
        Sentry.withScope(scope => {
          scope.setExtra('provider', provider);
          Sentry.captureException(error);
        });

        handlePermanentTokenError({ dispatch, error });

        return { accessToken: '', expiresAt: '' };
      }
    },
    [dispatch],
  );

  return useAuth({
    appId: AUTH_APP_ID,
    appPath: AUTH_APP_PATH,
    authPath: AUTH_PATH,
    customAuthAPIBaseURL: `${CONFIG.accountUrl}api/v1`,
    getPermanentAccessToken,
    hasAPIKeysNotification,
    hasClickwrapAgreement: true,
    hasReferralCodeBox: true,
    referralCode,
    signOutFromAPI: signOutAndClearCookies,
  });
};
